import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Upload, Edit3, MapPin, RotateCcw, ZoomIn, ZoomOut, Maximize2, Minimize2, Save } from "lucide-react";
import { DeviceMarker } from "./DeviceMarker";
import { toast } from "react-toastify";
import { Button } from "../../ui";
import { useLocationStore } from "../../../store/useLocationStore";
import { getPreSignedUrlForDownload, getPreSignedUrlForUpload } from "../../../api/admin";
import {
  useGetDevicesForLocation,
  useGetLocation,
  useUpdateLocation,
} from "../../../queries/admin";
import { useDashboard } from "../../../pages/dashboard/hooks/useDashboard";
import { DeviceCoordinates } from "../../../pages/dashboard/types/dashboard";
import { useDeviceStore } from "../../../store/useDeviceStore";
import { useIsAdmin } from "../../../utils/auth";
import { debounce } from "lodash";

export interface Device {
  id: string;
  x: number;
  y: number;
  status: "active" | "inactive" | "warning" | "critical";
  name: string;
}

interface FloorPlanUploadProps {
  isEditMode?: boolean;
}

export function FloorPlanUpload({ isEditMode: externalEditMode = false }: FloorPlanUploadProps) {
  const { selectedLocationId: locationId } = useLocationStore();
  const { selectedDeviceId: deviceId, selectedDeviceIds: multiSelectedDeviceIds } =
    useDeviceStore();
  const {
    deviceCoordinates,
    saveDeviceCoordinates,
    mapViewState,
    saveMapViewState,
    isLoading: isDashboardLoading,
  } = useDashboard();
  const setDeviceCoordinatesOnly = useDashboard((state) => state.setDeviceCoordinatesOnly);
  const visibleDeviceCoordinates = deviceCoordinates?.filter((dc) => dc.isVisible);
  const { data: _locationDevices } = useGetDevicesForLocation(
    locationId && locationId !== "not-available" ? locationId : null,
  );
  const locationDevices = _locationDevices || [];
  const deviceMap = new Map(locationDevices.map((device) => [device.id, device]));
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | undefined>();
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<Set<string>>(new Set());
  const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);

  const handleDeviceSelect = (deviceId: string) => {
    setSelectedDeviceId(selectedDeviceId === deviceId ? undefined : deviceId);
  };

  const centerDevices = useCallback(
    (deviceIds?: string[]) => {
      if (!deviceCoordinates) return;

      const devicesToCenter = deviceIds || Array.from(selectedDeviceIds);
      if (devicesToCenter.length === 0) {
        // If no specific devices, center all visible devices
        const allVisibleDevices = deviceCoordinates.filter((dc) => dc.isVisible).map((dc) => dc.id);
        devicesToCenter.push(...allVisibleDevices);
      }

      // Calculate positions with gaps around center
      const centerX = 50;
      const centerY = 50;
      const gap = 8; // Gap between devices in percentage points

      const coordinatesToBeSaved = deviceCoordinates.map((dc) => {
        if (devicesToCenter.includes(dc.id)) {
          const deviceIndex = devicesToCenter.indexOf(dc.id);

          // Arrange devices in a grid pattern around center
          if (devicesToCenter.length === 1) {
            // Single device goes to center
            return { ...dc, x: centerX, y: centerY, isVisible: true };
          } else if (devicesToCenter.length === 2) {
            // Two devices: left and right of center
            const offsetX = deviceIndex === 0 ? -gap / 2 : gap / 2;
            return { ...dc, x: centerX + offsetX, y: centerY, isVisible: true };
          } else if (devicesToCenter.length <= 4) {
            // 3-4 devices: square pattern around center
            const positions = [
              { x: centerX - gap / 2, y: centerY - gap / 2 }, // top-left
              { x: centerX + gap / 2, y: centerY - gap / 2 }, // top-right
              { x: centerX - gap / 2, y: centerY + gap / 2 }, // bottom-left
              { x: centerX + gap / 2, y: centerY + gap / 2 }, // bottom-right
            ];
            const pos = positions[deviceIndex];
            return { ...dc, x: pos.x, y: pos.y, isVisible: true };
          } else {
            // Many devices: circular pattern
            const angle = (deviceIndex / devicesToCenter.length) * 2 * Math.PI;
            const radius = Math.min(gap * 1.5, 15); // Limit radius to prevent going off-screen
            const x = centerX + Math.cos(angle) * radius;
            const y = centerY + Math.sin(angle) * radius;
            return {
              ...dc,
              x: Math.max(5, Math.min(95, x)),
              y: Math.max(5, Math.min(95, y)),
              isVisible: true,
            };
          }
        }
        return dc;
      });

      saveDeviceCoordinates(coordinatesToBeSaved, deviceId);
      toast.success(
        `Centered ${devicesToCenter.length} device${
          devicesToCenter.length !== 1 ? "s" : ""
        } with spacing`,
      );
    },
    [deviceCoordinates, selectedDeviceIds, saveDeviceCoordinates, deviceId],
  );

  const toggleDeviceVisibility = useCallback((deviceId: string) => {
    setSelectedDeviceIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(deviceId)) {
        newSet.delete(deviceId);
      } else {
        newSet.add(deviceId);
      }
      return newSet;
    });
  }, []);
  const { data: location } = useGetLocation(locationId);
  const { mutate: updateLocation } = useUpdateLocation();
  const isAdmin = useIsAdmin();
  const [floorPlanImage, setFloorPlanImage] = useState<string | null>(null);
  const [imageKey, setImageKey] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Zoom and pan state — initialized from saved map view state
  const [scale, setScale] = useState(mapViewState?.scale ?? 1);
  const [translate, setTranslate] = useState({
    x: mapViewState?.translateX ?? 0,
    y: mapViewState?.translateY ?? 0,
  });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0 });
  const translateStart = useRef({ x: 0, y: 0 });

  // Natural image size + container size, used to letterbox the image at its
  // original aspect ratio instead of stretching it to fill the container
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(
    null,
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setContainerSize({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Contain-fit: largest size that fits the container while preserving aspect ratio
  const fittedSize = useMemo(() => {
    if (!naturalSize || !containerSize) return null;
    const ratio = Math.min(
      containerSize.width / naturalSize.width,
      containerSize.height / naturalSize.height,
    );
    return { width: naturalSize.width * ratio, height: naturalSize.height * ratio };
  }, [naturalSize, containerSize]);

  // Sync local state when saved mapViewState changes (e.g., after dashboard load)
  const lastSyncedRef = useRef({ scale: mapViewState?.scale, tx: mapViewState?.translateX, ty: mapViewState?.translateY });
  useEffect(() => {
    const s = mapViewState?.scale ?? 1;
    const tx = mapViewState?.translateX ?? 0;
    const ty = mapViewState?.translateY ?? 0;
    if (s !== lastSyncedRef.current.scale || tx !== lastSyncedRef.current.tx || ty !== lastSyncedRef.current.ty) {
      setScale(s);
      setTranslate({ x: tx, y: ty });
      lastSyncedRef.current = { scale: s, tx, ty };
    }
  }, [mapViewState]);

  // Mirror of the current view for use inside native event handlers
  const viewRef = useRef({ scale, tx: translate.x, ty: translate.y });
  useEffect(() => {
    viewRef.current = { scale, tx: translate.x, ty: translate.y };
  }, [scale, translate]);

  // Cursor zoom: scroll wheel / trackpad pinch zooms toward the cursor.
  // Registered natively with passive: false so the page doesn't scroll and
  // the browser's own pinch-zoom doesn't kick in over the map.
  // Zooming is only allowed in the settings (Widgets) editor — the dashboard
  // shows the saved view at a fixed zoom level.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      // Dashboard: zoom is locked, so let the wheel scroll the page normally
      if (!externalEditMode) return;
      e.preventDefault();
      const { scale: s, tx, ty } = viewRef.current;
      // Trackpad pinch arrives as ctrl+wheel with larger deltas
      const zoomIntensity = e.ctrlKey ? 0.01 : 0.002;
      const next = Math.min(5, Math.max(0.5, s * Math.exp(-e.deltaY * zoomIntensity)));
      if (next === s) return;
      // Keep the image point under the cursor fixed while scaling
      const rect = el.getBoundingClientRect();
      const px = e.clientX - rect.left - rect.width / 2;
      const py = e.clientY - rect.top - rect.height / 2;
      const ratio = next / s;
      setScale(next);
      setTranslate({ x: px - (px - tx) * ratio, y: py - (py - ty) * ratio });
    };
    const preventPinch = (e: TouchEvent) => {
      if (e.touches.length >= 2) e.preventDefault();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchmove", preventPinch, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchmove", preventPinch);
    };
  }, [externalEditMode]);

  // Double-click fits the image back into the widget (contain-fit) — editor only,
  // since it changes the zoom level
  const handleDoubleClick = useCallback(() => {
    if (!externalEditMode) return;
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, [externalEditMode]);

  const handlePanStart = useCallback(
    (e: React.MouseEvent) => {
      // Only pan with middle mouse button or when not in edit mode on left click
      if (e.button === 1 || (e.button === 0 && !isEditMode)) {
        e.preventDefault();
        setIsPanning(true);
        panStart.current = { x: e.clientX, y: e.clientY };
        translateStart.current = { ...translate };
      }
    },
    [isEditMode, translate],
  );

  const handlePanMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isPanning) return;
      const dx = e.clientX - panStart.current.x;
      const dy = e.clientY - panStart.current.y;
      setTranslate({ x: translateStart.current.x + dx, y: translateStart.current.y + dy });
    },
    [isPanning],
  );

  const handlePanEnd = useCallback(() => {
    setIsPanning(false);
  }, []);

  const handleZoomIn = useCallback(() => {
    setScale((prev) => Math.min(5, +(prev + 0.1).toFixed(2)));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prev) => Math.max(0.5, +(prev - 0.1).toFixed(2)));
  }, []);

  const handleResetView = useCallback(() => {
    const saved = useDashboard.getState().mapViewState;
    setScale(saved?.scale ?? 1);
    setTranslate({ x: saved?.translateX ?? 0, y: saved?.translateY ?? 0 });
  }, []);

  const handleSaveView = useCallback(() => {
    saveMapViewState({ scale, translateX: translate.x, translateY: translate.y }, deviceId);
    toast.success("Map view saved");
  }, [scale, translate, saveMapViewState, deviceId]);

  // Exit the expanded (fullscreen) map with Escape
  useEffect(() => {
    if (!isExpanded) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsExpanded(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isExpanded]);

  const useDebouncedSave = () => {
    return useMemo(
      () =>
        debounce((deviceId: string) => {
          // Just save to API, local state is already updated
          const { saveDashboard } = useDashboard.getState();
          saveDashboard(deviceId);
        }, 500),
      [],
    );
  };
  const debouncedSave = useDebouncedSave();

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!locationId || locationId === "not-available") {
      toast.error("Please select a location before uploading a floor plan");
      return;
    }

    if (!location) {
      toast.error("Location data not loaded yet. Please try again.");
      return;
    }

    try {
      const res = await getPreSignedUrlForUpload({
        contentType: file.type,
        key: `${locationId}/plan/${file.name}`,
      });

      const uploadResponse = await fetch(res.url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      if (!uploadResponse.ok) {
        toast.error("Failed to upload image to storage");
        return;
      }

      setImageKey(res.key);
      updateLocation({ ...location, planKey: res.key });

      if (!deviceCoordinates?.length) {
        const coords: DeviceCoordinates[] = locationDevices.map((device, index) => {
          const centerX = 50;
          const centerY = 50;
          const gap = 8;

          if (locationDevices.length === 1) {
            return { id: device.id, isVisible: true, x: centerX, y: centerY };
          } else if (locationDevices.length === 2) {
            const offsetX = index === 0 ? -gap / 2 : gap / 2;
            return { id: device.id, isVisible: true, x: centerX + offsetX, y: centerY };
          } else if (locationDevices.length <= 4) {
            const positions = [
              { x: centerX - gap / 2, y: centerY - gap / 2 },
              { x: centerX + gap / 2, y: centerY - gap / 2 },
              { x: centerX - gap / 2, y: centerY + gap / 2 },
              { x: centerX + gap / 2, y: centerY + gap / 2 },
            ];
            const pos = positions[index];
            return { id: device.id, isVisible: true, x: pos.x, y: pos.y };
          } else {
            const angle = (index / locationDevices.length) * 2 * Math.PI;
            const radius = Math.min(gap * 1.5, 15);
            const x = centerX + Math.cos(angle) * radius;
            const y = centerY + Math.sin(angle) * radius;
            return {
              id: device.id,
              isVisible: true,
              x: Math.max(5, Math.min(95, x)),
              y: Math.max(5, Math.min(95, y)),
            };
          }
        });
        saveDeviceCoordinates(coords, deviceId);
      }

      toast.success("Floor plan uploaded successfully");
    } catch (err) {
      console.error("Floor plan upload failed:", err);
      toast.error("Failed to upload floor plan. Please try again.");
    }
  };

  useEffect(() => {
    if (location && location.planKey) {
      setImageKey(location.planKey);
    }
  }, [location]);

  useEffect(() => {
    if (!imageKey) {
      return;
    }
    const fetchUrl = async () => {
      const res = await getPreSignedUrlForDownload({ key: imageKey });
      setFloorPlanImage(res.url);
    };

    fetchUrl();
  }, [imageKey]);

  // Reconcile location devices with deviceCoordinates so every device in the
  // location is rendered on the floor plan. Without this sync, devices added
  // after the initial upload (or present on a freshly-loaded dashboard whose
  // saved coordinates are stale) would never appear.
  useEffect(() => {
    if (isDashboardLoading) return;
    if (!locationId || locationId === "not-available") return;
    if (locationDevices.length === 0) return;

    const currentCoordinates = deviceCoordinates ?? [];
    const existingIds = new Set(currentCoordinates.map((dc) => dc.id));
    const validIds = new Set(locationDevices.map((d) => d.id));

    const missingDevices = locationDevices.filter((d) => !existingIds.has(d.id));
    const cleanedExisting = currentCoordinates.filter((dc) => validIds.has(dc.id));
    const hasStaleEntries = cleanedExisting.length !== currentCoordinates.length;

    if (missingDevices.length === 0 && !hasStaleEntries) return;

    // Place new devices in a small ring around the center so they don't all
    // overlap; the user can drag them in edit mode afterwards.
    const newEntries: DeviceCoordinates[] = missingDevices.map((d, idx) => {
      if (missingDevices.length === 1) {
        return { id: d.id, isVisible: true, x: 50, y: 50 };
      }
      const angle = (idx / missingDevices.length) * 2 * Math.PI;
      const radius = Math.min(15, 4 + missingDevices.length);
      return {
        id: d.id,
        isVisible: true,
        x: Math.max(5, Math.min(95, 50 + Math.cos(angle) * radius)),
        y: Math.max(5, Math.min(95, 50 + Math.sin(angle) * radius)),
      };
    });

    saveDeviceCoordinates([...cleanedExisting, ...newEntries], deviceId);
  }, [
    isDashboardLoading,
    locationId,
    locationDevices,
    deviceCoordinates,
    saveDeviceCoordinates,
    deviceId,
  ]);

  const handleDeviceMove = useCallback(
    (movedDeviceId: string, newX: number, newY: number) => {
      if (!isEditMode) return;

      // Read latest coordinates from store to avoid stale closure issues during drag
      const { deviceCoordinates: currentCoordinates, setDeviceCoordinatesOnly: setCoords } =
        useDashboard.getState();
      const coordinatesToBeSaved = currentCoordinates.map((dc) =>
        dc.id === movedDeviceId ? { ...dc, x: newX, y: newY, isVisible: true } : dc,
      );

      // Update local state immediately for smooth dragging, but debounce API saves
      setCoords(coordinatesToBeSaved);
      debouncedSave(deviceId); // deviceId from useDeviceStore (selected device), NOT the moved device
    },
    [isEditMode, deviceId, debouncedSave],
  );

  const handleDeviceMoveEnd = useCallback(
    (_movedDeviceId: string) => {
      // Flush debounced save immediately so the final position is persisted
      debouncedSave.flush();
    },
    [debouncedSave],
  );

  const toggleEditMode = useCallback(() => {
    if (isEditMode) {
      // Exiting edit mode — flush any pending saves, save map view state, and do a final save
      debouncedSave.flush();
      // Persist current zoom/pan as the saved map view state
      saveMapViewState({ scale, translateX: translate.x, translateY: translate.y }, deviceId);
      toast.success("Layout saved");
    }
    setIsEditMode((prev) => !prev);
  }, [isEditMode, deviceId, debouncedSave, scale, translate, saveMapViewState]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDeviceDropdown(false);
      }
    };

    if (showDeviceDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showDeviceDropdown]);

  const deviceCount = visibleDeviceCoordinates?.length ?? 0;

  return (
    <div
      className={`bg-transparent border border-border shadow-sm overflow-hidden flex flex-col ${
        isExpanded ? "fixed inset-0 z-[100] rounded-none" : "rounded-xl min-h-[620px]"
      }`}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageChange}
        className="hidden"
      />

      {/* Header / Toolbar — only in widget/settings section */}
      {externalEditMode && (
        <div className="flex items-center justify-end px-6 py-3 border-b border-gray-100 bg-white">
          <div className="flex items-center gap-2">
            {/* Device Visibility Dropdown */}
            {floorPlanImage && deviceCoordinates && deviceCoordinates.length > 0 && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowDeviceDropdown(!showDeviceDropdown)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  <MapPin className="h-3.5 w-3.5" />
                  Devices
                  <svg
                    className="h-3.5 w-3.5 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {showDeviceDropdown && (
                  <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                    <div className="px-3 py-2 border-b border-gray-100">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Toggle Visibility
                      </p>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {deviceCoordinates.map((dc) => {
                        const device = deviceMap.get(dc.id);
                        if (!device) return null;
                        return (
                          <button
                            key={dc.id}
                            onClick={() => {
                              const coordinatesToBeSaved = deviceCoordinates.map((coord) =>
                                coord.id === dc.id
                                  ? { ...coord, isVisible: !coord.isVisible }
                                  : coord,
                              );
                              saveDeviceCoordinates(coordinatesToBeSaved, deviceId);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <div
                              className={`w-3 h-3 rounded-sm border ${dc.isVisible ? "bg-blue-500 border-blue-500" : "border-gray-300"} flex items-center justify-center`}
                            >
                              {dc.isVisible && (
                                <svg
                                  className="h-2 w-2 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={3}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              )}
                            </div>
                            <span className="truncate">{device.name}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="border-t border-gray-100 px-3 py-2">
                      <button
                        onClick={() => centerDevices()}
                        className="w-full text-xs text-blue-600 hover:text-blue-700 font-medium text-left"
                      >
                        Center All Visible Devices
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Upload / Change Image */}
            {externalEditMode && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                <Upload className="h-3.5 w-3.5" />
                {floorPlanImage ? "Change Image" : "Upload"}
              </button>
            )}

            {/* Edit Mode Toggle */}
            {externalEditMode && floorPlanImage && (
              <button
                onClick={toggleEditMode}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  isEditMode
                    ? "bg-orange-100 text-orange-700 hover:bg-orange-200"
                    : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                }`}
              >
                <Edit3 className="h-3.5 w-3.5" />
                {isEditMode ? "Save Layout" : "Edit Layout"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div
        ref={containerRef}
        className="relative w-full flex-1 overflow-hidden bg-transparent"
        style={{
          ...(isExpanded ? {} : { height: "540px" }),
          cursor: isPanning ? "grabbing" : floorPlanImage ? "grab" : "default",
          backgroundImage:
            "linear-gradient(to right, rgba(0,0,0,0.05) 1px, transparent 1px), " +
            "linear-gradient(to bottom, rgba(0,0,0,0.05) 1px, transparent 1px), " +
            "linear-gradient(to right, rgba(0,0,0,0.1) 1px, transparent 1px), " +
            "linear-gradient(to bottom, rgba(0,0,0,0.1) 1px, transparent 1px)",
          backgroundSize: "20px 20px, 20px 20px, 100px 100px, 100px 100px",
        }}
        onMouseDown={handlePanStart}
        onMouseMove={handlePanMove}
        onMouseUp={handlePanEnd}
        onMouseLeave={handlePanEnd}
        onDoubleClick={handleDoubleClick}
      >
        {floorPlanImage ? (
          <>
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
                transformOrigin: "center center",
                transition: isPanning ? "none" : "transform 0.15s ease-out",
              }}
            >
              {/* Sized to the image's original aspect ratio (contain-fit) so the
                  image is never stretched; markers use % of this wrapper so they
                  stay anchored to the image itself */}
              <div
                data-container="floor-plan"
                className="relative"
                style={
                  fittedSize
                    ? { width: fittedSize.width, height: fittedSize.height }
                    : undefined
                }
              >
                <img
                  src={floorPlanImage}
                  alt="Floor plan"
                  className="h-full w-full select-none pointer-events-none"
                  draggable={false}
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
                  }}
                />

                {/* Device Markers */}
                {visibleDeviceCoordinates &&
                  visibleDeviceCoordinates.map((coordinates) => {
                    const deviceFromMap = deviceMap.get(coordinates.id);
                    if (!deviceFromMap) return null;
                    const device: Device = {
                      id: deviceFromMap.id,
                      name: deviceFromMap.name,
                      status: "active",
                      x: coordinates.x,
                      y: coordinates.y,
                    };
                    const isInMultiSelection = multiSelectedDeviceIds.includes(coordinates.id);
                    return (
                      <DeviceMarker
                        key={coordinates.id}
                        device={device}
                        isEditMode={isEditMode}
                        isSelected={selectedDeviceId === coordinates.id || isInMultiSelection}
                        onMove={handleDeviceMove}
                        onMoveEnd={handleDeviceMoveEnd}
                        onClick={() => handleDeviceSelect(coordinates.id)}
                      />
                    );
                  })}
              </div>
            </div>

            {/* Zoom Controls — bottom-right in dashboard, top-right in settings */}
            <div className={`absolute ${externalEditMode ? "top-3 right-3" : "bottom-3 right-3"} flex flex-col gap-1 z-10`}>
              {externalEditMode && (
                <>
                  <button
                    onClick={handleZoomIn}
                    className="p-1.5 bg-white/90 hover:bg-white border border-gray-200 rounded-md shadow-sm text-gray-600 hover:text-gray-900 transition-colors"
                    title="Zoom in"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleZoomOut}
                    className="p-1.5 bg-white/90 hover:bg-white border border-gray-200 rounded-md shadow-sm text-gray-600 hover:text-gray-900 transition-colors"
                    title="Zoom out"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </button>
                </>
              )}
              <button
                onClick={handleResetView}
                className="p-1.5 bg-white/90 hover:bg-white border border-gray-200 rounded-md shadow-sm text-gray-600 hover:text-gray-900 transition-colors"
                title="Reset view"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              {isAdmin && (
                <button
                  onClick={handleSaveView}
                  className="p-1.5 bg-white/90 hover:bg-white border border-gray-200 rounded-md shadow-sm text-gray-600 hover:text-gray-900 transition-colors"
                  title="Save current view as default"
                >
                  <Save className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={() => setIsExpanded((prev) => !prev)}
                className="p-1.5 bg-white/90 hover:bg-white border border-gray-200 rounded-md shadow-sm text-gray-600 hover:text-gray-900 transition-colors"
                title={isExpanded ? "Exit fullscreen" : "Expand map"}
              >
                {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </button>
            </div>

            {/* Edit Mode Overlay */}
            {isEditMode && (
              <div className="absolute inset-0 bg-orange-500/5 border-2 border-orange-300/50 pointer-events-none">
                <div className="absolute top-3 left-3 bg-orange-50 text-orange-700 px-3 py-1.5 rounded-lg text-sm font-medium border border-orange-200 shadow-sm">
                  <Edit3 className="h-4 w-4 inline mr-2" />
                  Edit Mode Active
                </div>
              </div>
            )}
          </>
        ) : externalEditMode ? (
          <div className="absolute inset-4 border-2 border-dashed border-gray-300 hover:border-blue-300 bg-gray-50/50 hover:bg-blue-50/20 rounded-lg transition-all duration-200 flex flex-col items-center justify-center text-center p-8">
            <div className="p-4 bg-gray-50 rounded-full mb-4">
              <Upload className="h-12 w-12 text-gray-400" />
            </div>
            <p className="text-gray-600 mb-4 max-w-md text-sm">
              Upload an image of your building floor plan to place and manage IoT device markers
            </p>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
              onClick={() => fileInputRef.current?.click()}
            >
              Choose Image File
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <div className="p-4 bg-gray-50 rounded-full mb-4">
              <MapPin className="h-12 w-12 text-gray-300" />
            </div>
            <p className="text-gray-500 text-sm">
              No floor plan uploaded. Configure in widget settings.
            </p>
          </div>
        )}
      </div>

      {externalEditMode && isEditMode && (
        <div className="mx-4 mt-4 mb-2 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="p-1 bg-orange-100 rounded">
              <Edit3 className="h-4 w-4 text-orange-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-orange-800 mb-1">Edit Mode Active</p>
              <p className="text-sm text-orange-700">
                Drag devices to reposition them, or right-click to delete. Click Save Layout when
                finished.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="bg-gray-50/50 border-t border-gray-100 px-6 py-3">
        <div className="flex items-center text-sm text-gray-900">
          <span className="font-medium">
            {deviceCount} {deviceCount === 1 ? "Device" : "Devices"}
          </span>
          {location?.name && (
            <>
              <span className="mx-2 text-gray-400">&bull;</span>
              <span className="text-gray-900">{location.name}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
